import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileDown, FileText } from "lucide-react";

const SRC_LABEL = { fresh_unit: "Fresh Unit", damaged_set: "Damaged Set", refurbished_set: "Refurbished Set" };

export default function PartsTakenOutReport() {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    start: "", end: "", source_type: "", brand: "", model: "", part: "", complaint: "", approved_by: "", taken_out_by: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/spares/reports/parts-taken-out", { params });
      setRows(data.rows || []); setCount(data.count || 0);
    } catch { setRows([]); setCount(0); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const upd = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const download = (fmt) => {
    const qs = new URLSearchParams({ fmt, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) }).toString();
    downloadBlob({
      url: `/spares/reports/parts-taken-out?${qs}`,
      filename: `parts_taken_out.${fmt}`,
    });
  };

  return (
    <div className="space-y-5" data-testid="parts-taken-out-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Parts Taken Out Report</h1>
        <p className="text-sm text-slate-500 mt-1">Parts taken from fresh / damaged / refurbished units, with Service Head approval.</p>
      </div>
      <Card><CardContent className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <div><Label className="text-xs uppercase">From</Label><Input type="date" value={filters.start} onChange={(e) => upd("start", e.target.value)} data-testid="pto-filter-start" /></div>
          <div><Label className="text-xs uppercase">To</Label><Input type="date" value={filters.end} onChange={(e) => upd("end", e.target.value)} data-testid="pto-filter-end" /></div>
          <div><Label className="text-xs uppercase">Source</Label>
            <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm bg-white"
                    value={filters.source_type} onChange={(e) => upd("source_type", e.target.value)} data-testid="pto-filter-source">
              <option value="">All</option>
              {Object.entries(SRC_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div><Label className="text-xs uppercase">Brand</Label><Input value={filters.brand} onChange={(e) => upd("brand", e.target.value)} data-testid="pto-filter-brand" /></div>
          <div><Label className="text-xs uppercase">Model</Label><Input value={filters.model} onChange={(e) => upd("model", e.target.value)} data-testid="pto-filter-model" /></div>
          <div><Label className="text-xs uppercase">Part</Label><Input value={filters.part} onChange={(e) => upd("part", e.target.value)} data-testid="pto-filter-part" /></div>
          <div><Label className="text-xs uppercase">Complaint #</Label><Input value={filters.complaint} onChange={(e) => upd("complaint", e.target.value)} data-testid="pto-filter-complaint" /></div>
          <div><Label className="text-xs uppercase">Approved By</Label><Input value={filters.approved_by} onChange={(e) => upd("approved_by", e.target.value)} data-testid="pto-filter-approver" /></div>
          <div><Label className="text-xs uppercase">Taken Out By</Label><Input value={filters.taken_out_by} onChange={(e) => upd("taken_out_by", e.target.value)} data-testid="pto-filter-taker" /></div>
          <div className="flex items-end"><Button onClick={load} className="bg-slate-900 text-white w-full" data-testid="pto-apply">Apply</Button></div>
        </div>
        <div className="mt-3 flex gap-2 justify-end">
          <Button size="sm" variant="outline" onClick={() => download("xlsx")} data-testid="pto-xlsx"><FileDown size={14} className="mr-1" />Excel</Button>
          <Button size="sm" variant="outline" onClick={() => download("pdf")} data-testid="pto-pdf"><FileText size={14} className="mr-1" />PDF</Button>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-0">
        <div className="px-4 pt-3 text-xs text-slate-500">{count} record{count === 1 ? "" : "s"}</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <Th>Date</Th><Th>Complaint</Th><Th>Source</Th>
                <Th>Brand/Product/Model</Th><Th>Serial</Th>
                <Th>Part</Th><Th align="right">Qty</Th>
                <Th>Approved By</Th><Th>Taken By</Th><Th>Remarks</Th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="text-center py-12 text-slate-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={10} className="text-center py-12 text-slate-400">No records.</td></tr>}
              {rows.map((r, i) => (
                <tr key={i} data-testid={`pto-row-${i}`}>
                  <td className="text-xs">{r.date}</td>
                  <td className="font-mono">{r.complaint_number || "—"}</td>
                  <td>{SRC_LABEL[r.source_type] || r.source_type}</td>
                  <td>{r.source_brand}<div className="text-xs text-slate-500">{r.source_product} {r.source_model}</div></td>
                  <td className="font-mono text-xs">{r.source_serial || "—"}</td>
                  <td>{r.part_name}{r.part_code && <span className="font-mono text-xs text-slate-500 ml-1">({r.part_code})</span>}</td>
                  <td className="text-right font-mono">{r.quantity}</td>
                  <td>{r.approved_by || "—"}</td>
                  <td>{r.taken_out_by || "—"}</td>
                  <td className="text-xs text-slate-600">{r.remarks || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>
    </div>
  );
}
function Th({ children, align }) {
  return <th className={`${align === "right" ? "text-right" : "text-left"} text-xs uppercase tracking-wide text-slate-600 font-semibold px-2 py-2`}>{children}</th>;
}
